import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { useArmControl } from "providers/ArmControlProvider";
import { useToast } from "providers/ToastProvider";
import {
    ARM_STICK_MODE_ROTATE,
    ARM_STICK_MODE_TRANSLATE,
    MOVEJ_PROFILE_LIMITS,
    PRESS_OFFSET_LIMITS,
} from "lib/consts/arm";

const PANEL_W = 256; // w-64
const PANEL_H_EST = 510;

const RANGE_CLASS =
    "h-3 w-full cursor-pointer bg-transparent appearance-none accent-primary2 outline-none " +
    "[&::-webkit-slider-runnable-track]:h-1 [&::-webkit-slider-runnable-track]:rounded-full " +
    "[&::-webkit-slider-runnable-track]:bg-black70 " +
    "[&::-webkit-slider-thumb]:-mt-0.5 [&::-webkit-slider-thumb]:h-2.5 [&::-webkit-slider-thumb]:w-2.5 " +
    "[&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:appearance-none " +
    "[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-0 " +
    "[&::-webkit-slider-thumb]:bg-primary2 " +
    "[&::-moz-range-track]:h-1 [&::-moz-range-track]:rounded-full [&::-moz-range-track]:bg-black70 " +
    "[&::-moz-range-thumb]:h-2.5 [&::-moz-range-thumb]:w-2.5 [&::-moz-range-thumb]:cursor-pointer " +
    "[&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-primary2";
const MARGIN = 16;
/** Keep below control top bar (`h-topBar` ≈ 5rem). */
const TOP_BAR_CLEARANCE_PX = 88;
/** Bump key so prior top-left default does not stick in localStorage. */
const POS_STORAGE_KEY = "arm-control-panel-pos-v2";
const PRESET_NAME_RE = /^[A-Za-z0-9_-]{1,32}$/;
/** Built-in presets: Go only (no Del) — matches server BUILTIN_PRESET_NAMES. */
const BUILTIN_PRESET_NAMES = new Set(["stow", "ready", "go_zero"]);

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/**
 * Default: left column, vertically between mini-map and left joystick
 * (see ControlPage MiniView + JoyStickRow layout).
 */
function getDefaultPos() {
    if (typeof window === "undefined") {
        return { x: MARGIN, y: TOP_BAR_CLEARANCE_PX };
    }
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const vh = window.innerHeight;
    const vw = window.innerWidth;

    // ButtonsAndViewsGroup: left-5 top-5; MiniView default ~16.75rem × 12.5rem
    const mapTop = rem * 1.25;
    const mapBtnH = rem * 3;
    const mapH = rem * 12.5;
    const mapBottom = mapTop + mapBtnH + mapH;

    // JoyStickRow: absolute bottom-10; pad for bottom bar
    const joyPadBottom = rem * 2.5 + rem * 10;
    const joyH = rem * 11;
    const joyTop = vh - joyPadBottom - joyH;

    const gapTop = mapBottom + MARGIN;
    const gapBottom = joyTop - MARGIN;
    let y;
    if (gapBottom - gapTop >= PANEL_H_EST) {
        y = Math.round((gapTop + gapBottom - PANEL_H_EST) / 2);
    } else {
        y = Math.max(TOP_BAR_CLEARANCE_PX, Math.round(gapTop));
    }

    const x = Math.round(rem * 1.25); // left-5
    const maxX = Math.max(MARGIN, vw - PANEL_W - MARGIN);
    const maxY = Math.max(TOP_BAR_CLEARANCE_PX, vh - PANEL_H_EST - MARGIN);
    return {
        x: clamp(x, MARGIN, maxX),
        y: clamp(y, TOP_BAR_CLEARANCE_PX, maxY),
    };
}

function readStoredPos() {
    if (typeof window === "undefined") return getDefaultPos();
    try {
        const raw = window.localStorage.getItem(POS_STORAGE_KEY);
        if (!raw) return getDefaultPos();
        const parsed = JSON.parse(raw);
        const x = Number(parsed?.x);
        const y = Number(parsed?.y);
        if (!Number.isFinite(x) || !Number.isFinite(y)) return getDefaultPos();
        return { x, y };
    } catch {
        return getDefaultPos();
    }
}

/**
 * Compact arm status + discrete controls. Shown while arm control mode is active.
 * Draggable via the title bar so it does not block the joysticks.
 */
const ArmControlPanel = ({ visible = false, className = "" }) => {
    const { t } = useTranslation("control");
    const { addToast } = useToast();
    const {
        armState,
        armStickMode,
        setArmStickMode,
        stop,
        goZero,
        gripperOpen,
        gripperClose,
        savePreset,
        gotoPreset,
        deletePreset,
        presetNames,
        movejProfile,
        setMovejProfile,
        pressOffsets,
        setPressOffsets,
    } = useArmControl();

    const panelRef = useRef(null);
    const [floatPos, setFloatPos] = useState(readStoredPos);
    const [posReady, setPosReady] = useState(false);
    const [presetNameInput, setPresetNameInput] = useState("");
    const [presetBusy, setPresetBusy] = useState(false);

    const clampToViewport = useCallback((pos) => {
        const el = panelRef.current;
        const w = el?.offsetWidth ?? PANEL_W;
        const h = el?.offsetHeight ?? 220;
        const maxX = Math.max(MARGIN, window.innerWidth - w - MARGIN);
        const maxY = Math.max(TOP_BAR_CLEARANCE_PX, window.innerHeight - h - MARGIN);
        return {
            x: clamp(pos.x, MARGIN, maxX),
            y: clamp(pos.y, TOP_BAR_CLEARANCE_PX, maxY),
        };
    }, []);

    useLayoutEffect(() => {
        if (!visible) {
            setPosReady(false);
            return;
        }
        setFloatPos((prev) => clampToViewport(prev));
        setPosReady(true);
    }, [visible, clampToViewport]);

    useEffect(() => {
        if (!visible || typeof window === "undefined") return;
        window.localStorage.setItem(POS_STORAGE_KEY, JSON.stringify(floatPos));
    }, [floatPos, visible]);

    useEffect(() => {
        if (!visible) return;
        const onResize = () => setFloatPos((prev) => clampToViewport(prev));
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [visible, clampToViewport]);

    const onDragHandlePointerDown = (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();

        const originX = floatPos.x;
        const originY = floatPos.y;
        const startClientX = e.clientX;
        const startClientY = e.clientY;

        const onMove = (ev) => {
            setFloatPos(
                clampToViewport({
                    x: originX + (ev.clientX - startClientX),
                    y: originY + (ev.clientY - startClientY),
                })
            );
        };

        const onUp = () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
            window.removeEventListener("pointercancel", onUp);
        };

        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
        window.addEventListener("pointercancel", onUp);
    };

    const onSavePosition = async () => {
        const name = presetNameInput.trim();
        if (!PRESET_NAME_RE.test(name)) {
            addToast({ type: "error", message: t("arm.invalidPositionName") });
            return;
        }
        if (presetNames.includes(name)) {
            const ok = window.confirm(t("arm.overwritePositionConfirm", { name }));
            if (!ok) return;
        }
        setPresetBusy(true);
        try {
            await savePreset(name);
            setPresetNameInput("");
        } catch (err) {
            console.error("savePreset failed", err);
            addToast({ type: "error", message: t("arm.savePositionFailed") });
        } finally {
            setPresetBusy(false);
        }
    };

    const onGotoPosition = async (name) => {
        setPresetBusy(true);
        try {
            await gotoPreset(name);
        } catch (err) {
            console.error("gotoPreset failed", err);
            addToast({ type: "error", message: t("arm.goPositionFailed") });
        } finally {
            setPresetBusy(false);
        }
    };

    const onDeletePosition = async (name) => {
        const ok = window.confirm(t("arm.deletePositionConfirm", { name }));
        if (!ok) return;
        setPresetBusy(true);
        try {
            await deletePreset(name);
        } catch (err) {
            console.error("deletePreset failed", err);
            addToast({ type: "error", message: t("arm.deletePositionFailed") });
        } finally {
            setPresetBusy(false);
        }
    };

    if (!visible) return null;

    const busy = Boolean(armState?.busy);
    const demo = Boolean(armState?.demo);
    const ee = armState?.ee?.position;
    const grip = armState?.gripper?.position;

    const btn =
        "rounded-r5 bg-black70 px-2 py-1 text-h5 hover:bg-black80 disabled:opacity-40";
    const modeBtn = (active) =>
        twMerge(
            "flex-1 rounded-r5 px-2 py-1 text-h5",
            active
                ? "bg-primary2 text-white"
                : "bg-black70 text-black40 hover:bg-black80 hover:text-white"
        );
    const isTranslate = armStickMode === ARM_STICK_MODE_TRANSLATE;

    return (
        <div
            ref={panelRef}
            className={twMerge(
                "pointer-events-auto fixed z-[100] w-64 rounded-r10 border border-black80 bg-black90 text-white shadow-lg",
                !posReady && "invisible",
                className
            )}
            style={{ left: floatPos.x, top: floatPos.y }}
        >
            <div
                className="flex cursor-grab touch-none select-none items-center gap-2 border-b border-black80 bg-black100/80 px-3 py-2 active:cursor-grabbing"
                onPointerDown={onDragHandlePointerDown}
            >
                <p className="min-w-0 flex-1 text-h5 font-semibold">{t("arm.title")}</p>
                <p className="shrink-0 text-h5 text-black40">
                    {demo ? t("arm.demo") : t("arm.live")}
                    {busy ? ` · ${t("arm.busy")}` : ""}
                </p>
            </div>

            <div className="space-y-2 p-3">
                {ee && (
                    <p className="text-h5 text-black40">
                        EE [{ee[0]?.toFixed?.(2) ?? ee[0]}, {ee[1]?.toFixed?.(2) ?? ee[1]},{" "}
                        {ee[2]?.toFixed?.(2) ?? ee[2]}]
                        {grip != null ? ` · g ${Number(grip).toFixed(2)}` : ""}
                    </p>
                )}
                <div className="flex gap-1 rounded-r5 border border-black80 bg-black100 p-0.5">
                    <button
                        type="button"
                        className={modeBtn(isTranslate)}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={() => setArmStickMode(ARM_STICK_MODE_TRANSLATE)}
                    >
                        {t("arm.modeTranslate")}
                    </button>
                    <button
                        type="button"
                        className={modeBtn(!isTranslate)}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={() => setArmStickMode(ARM_STICK_MODE_ROTATE)}
                    >
                        {t("arm.modeRotate")}
                    </button>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        className={btn}
                        // Stop must work while busy (press/IBVS) — that is the point.
                        onClick={() => stop()}
                    >
                        {t("arm.stop")}
                    </button>
                    <button type="button" className={btn} disabled={busy} onClick={() => goZero()}>
                        {t("arm.goZero")}
                    </button>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        className={btn}
                        disabled={busy}
                        onClick={() => gripperOpen()}
                    >
                        {t("arm.gripperOpen")}
                    </button>
                    <button
                        type="button"
                        className={btn}
                        disabled={busy}
                        onClick={() => gripperClose()}
                    >
                        {t("arm.gripperClose")}
                    </button>
                </div>
                <div
                    className="space-y-1.5 rounded-r5 border border-black80 bg-black100/50 p-2"
                    onPointerDown={(e) => e.stopPropagation()}
                >
                    <p className="text-h5 text-black40">{t("arm.movejProfile")}</p>
                    {[
                        {
                            key: "speed",
                            label: t("arm.movejSpeed"),
                            format: (v) => `${Math.round(v * 100)}%`,
                        },
                        {
                            key: "aggressiveness",
                            label: t("arm.movejAggressiveness"),
                            format: (v) => v.toFixed(2),
                        },
                        {
                            key: "damping",
                            label: t("arm.movejDamping"),
                            format: (v) => v.toFixed(2),
                        },
                    ].map(({ key, label, format }) => {
                        const limits = MOVEJ_PROFILE_LIMITS[key];
                        const value = Number(movejProfile?.[key] ?? limits.min);
                        return (
                            <div key={key} className="space-y-0.5">
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-h5 text-black40">{label}</span>
                                    <span className="font-mono text-h5 tabular-nums text-white">
                                        {format(value)}
                                    </span>
                                </div>
                                <input
                                    type="range"
                                    min={limits.min}
                                    max={limits.max}
                                    step={limits.step}
                                    value={value}
                                    aria-label={label}
                                    onChange={(e) => {
                                        const next = Number.parseFloat(e.target.value);
                                        if (Number.isFinite(next)) {
                                            setMovejProfile({ [key]: next });
                                        }
                                    }}
                                    className={RANGE_CLASS}
                                />
                            </div>
                        );
                    })}
                </div>
                <div
                    className="space-y-1.5 rounded-r5 border border-black80 bg-black100/50 p-2"
                    onPointerDown={(e) => e.stopPropagation()}
                >
                    <p className="text-h5 text-black40">{t("arm.pressOffsets")}</p>
                    {[
                        {
                            key: "tip_offset_x_m",
                            label: t("arm.tipOffsetX"),
                        },
                        {
                            key: "tip_offset_y_m",
                            label: t("arm.tipOffsetY"),
                        },
                    ].map(({ key, label }) => {
                        const limits = PRESS_OFFSET_LIMITS[key];
                        const value = Number(pressOffsets?.[key] ?? limits.min);
                        return (
                            <div key={key} className="space-y-0.5">
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-h5 text-black40">{label}</span>
                                    <span className="font-mono text-h5 tabular-nums text-white">
                                        {Math.round(value * 1000)} mm
                                    </span>
                                </div>
                                <input
                                    type="range"
                                    min={limits.min}
                                    max={limits.max}
                                    step={limits.step}
                                    value={value}
                                    aria-label={label}
                                    onChange={(e) => {
                                        const next = Number.parseFloat(e.target.value);
                                        if (Number.isFinite(next)) {
                                            setPressOffsets({ [key]: next });
                                        }
                                    }}
                                    className={RANGE_CLASS}
                                />
                            </div>
                        );
                    })}
                </div>
                <div className="space-y-1">
                    <p className="text-h5 text-black40">{t("arm.savedPositions")}</p>
                    <div className="flex gap-1">
                        <input
                            type="text"
                            value={presetNameInput}
                            onChange={(e) => setPresetNameInput(e.target.value)}
                            onPointerDown={(e) => e.stopPropagation()}
                            placeholder={t("arm.positionNamePlaceholder")}
                            maxLength={32}
                            disabled={busy || presetBusy}
                            className="min-w-0 flex-1 rounded-r5 border border-black80 bg-black70 px-2 py-1 text-h5 text-white placeholder:text-black60 focus:border-primary2 focus:outline-none"
                        />
                        <button
                            type="button"
                            className={btn}
                            disabled={busy || presetBusy || !presetNameInput.trim()}
                            onClick={onSavePosition}
                        >
                            {t("arm.savePosition")}
                        </button>
                    </div>
                    {presetNames.length > 0 ? (
                        <ul className="max-h-28 space-y-1 overflow-y-auto rounded-r5 border border-black80 bg-black100/50 p-1">
                            {presetNames.map((name) => (
                                <li
                                    key={name}
                                    className="flex items-center gap-1 rounded px-1 py-0.5 hover:bg-black80"
                                >
                                    <span className="min-w-0 flex-1 truncate text-h5 font-semibold">
                                        {name}
                                    </span>
                                    <button
                                        type="button"
                                        className={btn}
                                        disabled={busy || presetBusy}
                                        onClick={() => onGotoPosition(name)}
                                    >
                                        {t("arm.goPosition")}
                                    </button>
                                    {!BUILTIN_PRESET_NAMES.has(name) && (
                                        <button
                                            type="button"
                                            className={btn}
                                            disabled={busy || presetBusy}
                                            onClick={() => onDeletePosition(name)}
                                        >
                                            {t("arm.deletePosition")}
                                        </button>
                                    )}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-h5 text-black60">{t("arm.noSavedPositions")}</p>
                    )}
                </div>
                <p className="text-h5 text-black60">
                    {isTranslate ? t("arm.joystickHintTranslate") : t("arm.joystickHintRotate")}
                </p>
            </div>
        </div>
    );
};

export default ArmControlPanel;
